// ===========================================================================
// The visitor socket: what a page holds before it has a session.
//
// The landing used to poll while it had no session: the headcount every
// twenty seconds, the rooms every two, the announcement every two minutes, a
// check under review held open, a support thread every second. Now a page
// with no token opens a visitor socket (server/src/visitors.js) at the same
// door, is pushed what the landing shows and the channels it watches, and
// lets it go the moment a session socket opens. A page holds one socket,
// always: this one, or that one.
//
// It comes back on its own. A dropped socket rejoins with a jitter, because
// the usual cause is the server going away and every page is doing this in
// the same second. A refused one (the server's visitor cap) tries again after
// a growing wait, up to five minutes. Nothing here touches config or the DOM:
// the caller hands in how to make a socket and what to offer at the door, so
// it is unit-tested with a fake. ASCII only.
// ===========================================================================

export const REJOIN_MS = 800;
export const REJOIN_JITTER_MS = 2500;
export const REFUSED_MS = 30_000;
export const REFUSED_MAX_MS = 5 * 60_000;

/**
 * @param {object} o
 * @param {() => object} o.open  a fresh Signaling
 * @param {() => Promise<{ keepsakeKey: string|null, fingerprint: string }>} o.offer  what to offer at the door
 * @param {(sig: object) => void} o.wire  register the frame handlers on a socket before it connects
 * @param {(sig: object) => void} [o.onOpen]  the socket is carrying the page now
 * @param {(sig: object) => void} [o.onClose]  it no longer is
 * @param {{ setTimeout: Function, clearTimeout: Function }} [o.timers]
 * @param {() => number} [o.random]
 */
export function createVisitor({ open, offer, wire, onOpen = () => {}, onClose = () => {}, timers = globalThis, random = Math.random }) {
  let sock = null;
  let wanted = false;
  let retry = null;
  let refusals = 0;
  let connecting = null;

  function later(ms) {
    timers.clearTimeout(retry);
    retry = timers.setTimeout(() => {
      retry = null;
      connect();
    }, ms);
  }

  function connect() {
    if (!wanted || sock || connecting) return connecting || Promise.resolve();
    connecting = (async () => {
      const s = open();
      wire(s);
      s.on('__close', () => {
        if (sock !== s) return; // a socket that never opened, or was replaced
        sock = null;
        onClose(s);
        if (wanted) later(REJOIN_MS + random() * REJOIN_JITTER_MS);
      });
      let what;
      try {
        what = await offer();
      } catch {
        what = { keepsakeKey: null, fingerprint: '' };
      }
      if (!wanted) return;
      try {
        await s.connectVisitor(what);
      } catch {
        // Refused, or unreachable. Later, and later still each time.
        refusals += 1;
        if (wanted) later(Math.min(REFUSED_MS * 2 ** (refusals - 1), REFUSED_MAX_MS) + random() * REJOIN_JITTER_MS);
        return;
      }
      if (!wanted) {
        // A session socket opened while this was connecting: one socket.
        s.close();
        return;
      }
      refusals = 0;
      sock = s;
      onOpen(s);
    })().finally(() => {
      connecting = null;
    });
    return connecting;
  }

  return {
    /** The page has no session socket: hold a visitor socket, and keep holding one. */
    want() {
      wanted = true;
      return connect();
    },
    /** The page has a session socket now: let the visitor socket go. */
    stop() {
      wanted = false;
      timers.clearTimeout(retry);
      retry = null;
      const s = sock;
      sock = null;
      if (s) {
        s.close();
        onClose(s);
      }
    },
    current: () => sock,
    wanted: () => wanted,
  };
}

export default { createVisitor, REJOIN_MS, REJOIN_JITTER_MS, REFUSED_MS, REFUSED_MAX_MS };
